import { useState } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { useFormikContext } from 'formik';
import { captureException } from '@sentry/react';

import { Card } from 'components/Card';
import { Alert } from 'components/alert/Alert';
import { Button } from 'components/button/Button';
import { DataField } from 'components/DataField';
import { Dropzone } from 'components/Dropzone';
import { Dialog } from 'components/dialog/Dialog';
import { ConfirmDialog } from 'components/dialog/ConfirmDialog';
import { InputDialog } from 'components/dialog/InputDialog';
import { InputField } from 'components/input/InputField';
import { InputWrapper } from 'components/InputWrapper';
import { Table } from 'components/table/Table';
import { TextAreaField } from 'components/textarea/TextAreaField';
import { Pill, PillColor } from 'components/pill/Pill';
import { Formik } from 'components/form/formik';
import { useAuth } from 'contexts/auth-context';
import { roleToNumber } from 'src/app/auth/roles.client';
import { InternalRole } from '../../../auth/endpoints/MeEndpoint.types';
import { formatDateTime } from '@utils/date';
import { formatIban, isValidIban, isValidOptionalBic } from '@utils/iban';
import { getDisplayError } from '@utils/get-display-error';
import { useDownloadDocument } from '../../../document/hooks/useDownloadDocument';
import {
  requestSupplierBankAccountChange,
  reviewSupplierBankAccountChange,
  uploadBankAccountProof,
  useSupplierBankAccount,
  useSupplierIbanCheck,
} from '../../models';
import type {
  SupplierBankAccountChange,
  SupplierBankAccountChangeStatus,
} from '../../endpoints/SupplierBankAccountEndpoints.types';

const STATUS_LABELS: Record<SupplierBankAccountChangeStatus, string> = {
  Pending: 'Wacht op goedkeuring',
  Approved: 'Goedgekeurd',
  Rejected: 'Afgewezen',
  Cancelled: 'Geannuleerd',
};

const STATUS_COLORS: Record<SupplierBankAccountChangeStatus, PillColor> = {
  Pending: 'warning',
  Approved: 'positive',
  Rejected: 'negative',
  Cancelled: 'muted',
};

interface IRequestValues {
  iban: string;
  bic: string;
  reason: string;
  proof: globalThis.File | null;
}

const requestSchema = Yup.object().shape({
  iban: Yup.string()
    .required('Vereist')
    .test('iban', 'Ongeldige IBAN', (value) => isValidIban(value)),
  bic: Yup.string().test('bic', 'Ongeldige BIC', (value) => isValidOptionalBic(value)),
  reason: Yup.string().trim().required('Vereist'),
  proof: Yup.mixed().nullable(),
});

/** Live, non-blocking warnings (country mismatch, IBAN used elsewhere) for the IBAN being typed. */
const IbanWarnings: React.FC<{ supplierId: number; country: string }> = ({ supplierId, country }) => {
  const { values } = useFormikContext<IRequestValues>();
  const { data } = useSupplierIbanCheck(values.iban, country, supplierId);
  if (!data?.warnings.length) {
    return null;
  }
  return (
    <Alert type="warning" className="mb-4">
      {data.warnings.map((warning) => (
        <div key={warning}>{warning}</div>
      ))}
    </Alert>
  );
};

const ProofDropzone: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<IRequestValues>();
  return (
    <InputWrapper
      labelText="Bewijsstuk (optioneel)"
      helperText="Bv. een bankattest of een brief op briefpapier van de vervoerder."
      noLabel
      className="mb-4"
    >
      <Dropzone
        files={values.proof ? [values.proof] : []}
        onUpload={(files) => setFieldValue('proof', files[0] ?? null)}
        accept="application/pdf,image/*"
      />
    </InputWrapper>
  );
};

const RequestChangeDialog: React.FC<{
  supplierId: number;
  country: string;
  onClose: () => void;
  onDone: () => void;
}> = ({ supplierId, country, onClose, onDone }) => {
  return (
    <Dialog isOpen onClose={onClose} title="Wijziging bankrekening aanvragen" isDismissable={false}>
      <Formik<IRequestValues>
        initialValues={{ iban: '', bic: '', reason: '', proof: null }}
        validationSchema={requestSchema}
        onSubmit={async (values) => {
          try {
            const proofDocumentId = values.proof ? await uploadBankAccountProof(values.proof) : null;
            await requestSupplierBankAccountChange(supplierId, {
              iban: values.iban,
              bic: values.bic || null,
              reason: values.reason,
              proofDocumentId,
            });
            toast.success('Wijziging aangevraagd. Een collega moet ze nog goedkeuren.');
            onDone();
          } catch (err) {
            captureException(err);
            toast.error(`Kon wijziging niet aanvragen: ${getDisplayError(err)}`);
          }
        }}
      >
        {({ handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            <p className="text-sm text-fg-secondary mb-4">
              Nooit op basis van enkel een e-mail of telefoon. Een collega keurt de wijziging goed.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <InputField labelText="Nieuwe IBAN" type="text" name="iban" />
              <InputField labelText="BIC" type="text" name="bic" />
            </div>
            <IbanWarnings supplierId={supplierId} country={country} />
            <TextAreaField
              labelText="Reden en bewijs"
              helperText="Zonder bewijsstuk: beschrijf hoe de wijziging bevestigd werd."
              name="reason"
            />
            <ProofDropzone />
            <div className="flex justify-between">
              <Button onTrigger={onClose} isDisabled={isSubmitting}>
                Annuleer
              </Button>
              <Button type="submit" color="primary" isDisabled={isSubmitting} isLoading={isSubmitting}>
                Aanvragen
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

const PendingChange: React.FC<{ change: SupplierBankAccountChange; canReview: boolean; onDone: () => void }> = ({
  change,
  canReview,
  onDone,
}) => {
  const { me } = useAuth();
  const { downloadFile } = useDownloadDocument();
  const isRequester = change.requestedById === me.id;

  const review = async (action: 'approve' | 'reject' | 'cancel', note = '') => {
    await reviewSupplierBankAccountChange(change.id, { action, note });
    toast.success(
      action === 'approve'
        ? 'Bankrekening gewijzigd'
        : action === 'reject'
          ? 'Wijziging afgewezen'
          : 'Wijziging geannuleerd',
    );
    onDone();
  };

  return (
    <Alert type="warning">
      <div className="grid gap-2">
        <div className="font-medium">
          Wijziging wacht op goedkeuring: {formatIban(change.oldIban) || '-'} → {formatIban(change.newIban)}
          {change.newBic ? ` (${change.newBic})` : ''}
        </div>
        <div>
          Aangevraagd door {change.requestedByName} op {formatDateTime(change.requestedAt)}. Reden: {change.reason}
          {!change.proofDocument && ' (geen bewijsstuk)'}
        </div>
        <div className="flex flex-wrap gap-2">
          {change.proofDocument && (
            <Button size={8} onTrigger={() => downloadFile(change.proofDocument!)}>
              Bewijsstuk
            </Button>
          )}
          {isRequester ? (
            <ConfirmDialog
              triggerText="Annuleren"
              triggerSize={8}
              title="Wijziging annuleren?"
              description="De huidige IBAN blijft behouden."
              submitText="Annuleer wijziging"
              onSubmit={() => review('cancel')}
            />
          ) : (
            canReview && (
              <>
                <ConfirmDialog
                  triggerText="Goedkeuren"
                  triggerSize={8}
                  title="Nieuwe bankrekening goedkeuren?"
                  description={`Vanaf nu wordt er betaald op ${formatIban(change.newIban)}. Controleer ${change.proofDocument ? 'het bewijsstuk' : 'de reden'}.`}
                  submitText="Goedkeuren"
                  onSubmit={() => review('approve')}
                />
                <InputDialog
                  triggerText="Afwijzen"
                  triggerColor="danger"
                  triggerSize={8}
                  title="Wijziging afwijzen"
                  labelText="Reden"
                  submitText="Afwijzen"
                  onSubmit={(note) => review('reject', note)}
                />
              </>
            )
          )}
        </div>
      </div>
    </Alert>
  );
};

const HISTORY_HEADERS = [
  { id: 'status', name: 'Status' },
  { id: 'change', name: 'Wijziging' },
  { id: 'reason', name: 'Reden' },
  { id: 'people', name: 'Aangevraagd / beoordeeld door' },
  { id: 'date', name: 'Datum' },
];

const BankAccountHistory: React.FC<{ changes: SupplierBankAccountChange[] }> = ({ changes }) => {
  return (
    <Table<SupplierBankAccountChange>
      idKey="id"
      headers={HISTORY_HEADERS}
      data={changes}
      isRowClickable={false}
      mapData={(change) => [
        <Pill color={STATUS_COLORS[change.status]} size="sm">
          {STATUS_LABELS[change.status]}
        </Pill>,
        `${formatIban(change.oldIban) || '-'} → ${formatIban(change.newIban)}`,
        change.source === 'ExactImport' ? 'Import uit Exact' : change.reason,
        [change.requestedByName, change.reviewedByName].filter(Boolean).join(' / '),
        formatDateTime(change.reviewedAt ?? change.requestedAt),
      ]}
    />
  );
};

/**
 * Bank account of a carrier. Changes need a second person's approval and a proof
 * (document or described in the reason); the current IBAN stays in effect until then.
 */
export const SupplierBankAccountCard: React.FC<{ supplierId: number; country: string }> = ({ supplierId, country }) => {
  const { me } = useAuth();
  const { data, error, mutate } = useSupplierBankAccount(supplierId);
  const [isRequesting, setIsRequesting] = useState(false);
  const canEdit = roleToNumber(me.role) >= roleToNumber(InternalRole.InternalEmployeePlus);
  const pending = data?.changes.find((change) => change.status === 'Pending');
  const history = data?.changes.filter((change) => change.status !== 'Pending') ?? [];

  return (
    <Card
      title="Bankrekening"
      subtitle="Wijzigingen enkel via een aanvraag, goedgekeurd door een collega."
      rightHeading={
        canEdit &&
        !pending && (
          <Button size={8} onTrigger={() => setIsRequesting(true)}>
            Wijziging aanvragen
          </Button>
        )
      }
    >
      {error && <Alert type="error">{`Kon bankrekening niet laden: ${getDisplayError(error)}`}</Alert>}
      {data && (
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-4">
            <DataField title="IBAN">{formatIban(data.iban) || '-'}</DataField>
            <DataField title="BIC">{data.bic || '-'}</DataField>
          </div>
          {!data.ibanValid && (
            <Alert type="error">Geen geldige IBAN: self billing wordt niet aangemaakt of goedgekeurd.</Alert>
          )}
          {data.warnings.length > 0 && (
            <Alert type="warning">
              {data.warnings.map((warning) => (
                <div key={warning}>{warning}</div>
              ))}
            </Alert>
          )}
          {pending && <PendingChange change={pending} canReview={canEdit} onDone={() => mutate()} />}
          {history.length > 0 && <BankAccountHistory changes={history} />}
        </div>
      )}
      {isRequesting && (
        <RequestChangeDialog
          supplierId={supplierId}
          country={country}
          onClose={() => setIsRequesting(false)}
          onDone={() => {
            setIsRequesting(false);
            mutate();
          }}
        />
      )}
    </Card>
  );
};
